import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@/analytics';
import { ToastInfo, ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { renderElementToPngBlob } from '@/utils/copyElementAsImage';

interface UseCopyElementAsImageOptions {
    /** Reported to analytics as the `exportType` of the copy. */
    exportType: string;
    /** Any valid CSS color. Defaults to the theme's surface, see `@/utils/elementCapture`. */
    backgroundColor?: string;
}

/**
 * Copies a DOM subtree to the clipboard as a PNG, so it can be pasted into a document.
 *
 * Nodes marked with `data-copy-exclude` are left out of the capture — see
 * `@/utils/elementCapture`.
 */
export interface CopyElementAsImage {
    copyAsImage: (element: HTMLElement | null) => Promise<void>;
    isCopying: boolean;
    /** False where the browser cannot put an image on the clipboard. */
    isSupported: boolean;
}

export function useCopyElementAsImage(options: UseCopyElementAsImageOptions): CopyElementAsImage {
    const { exportType, backgroundColor } = options;
    const { t } = useTranslation();

    const [isCopying, setIsCopying] = useState(false);

    const isSupported = useMemo(
        () =>
            typeof window.ClipboardItem !== 'undefined' &&
            typeof navigator.clipboard?.write === 'function',
        []
    );

    const copyAsImage = useCallback(
        async (element: HTMLElement | null) => {
            if (!element || !isSupported) {
                return;
            }

            setIsCopying(true);

            try {
                // Safari drops the user activation as soon as the handler awaits, so the
                // ClipboardItem has to be constructed synchronously and handed the pending
                // render instead of an already-resolved blob.
                const clipboardItem = new window.ClipboardItem({
                    'image/png': renderElementToPngBlob(element, { backgroundColor }),
                });

                await navigator.clipboard.write([clipboardItem]);

                analytics.exportTriggered({ exportType, fileFormat: 'png' });

                ToastSuccess(t('molecules.copyAsImage.success'), 'copy-as-image-success');
            } catch (error) {
                console.error('Failed to copy element as image:', error);

                ToastInfo({
                    content: t('molecules.copyAsImage.error'),
                    status: 'error',
                    icon: 'noicon',
                    options: { toastId: 'copy-as-image-error', autoClose: 5000 },
                });
            } finally {
                setIsCopying(false);
            }
        },
        [backgroundColor, exportType, isSupported, t]
    );

    return { copyAsImage, isCopying, isSupported };
}
